import {
  Alert,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  message,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Tooltip,
} from "antd";
import { SizeType } from "antd/lib/config-provider/SizeContext";
import { Rule } from "antd/lib/form";
import { FormInstance, FormLayout, FormProps } from "antd/lib/form/Form";
import { Gutter } from "antd/lib/grid/row";
import { Moment } from "moment";
import React, { Fragment } from "react";
import {
  FormFieldConfig,
  RadioOptionsInterface,
  SelectOptionsInterface,
} from "../../helpers/shared-interfaces";
import { filterMethod, notProvidedText } from "../../helpers/utilities";
import DateSelector from "../DateSelector";
import FileUpload from "../FileUpload";
import PhoneNumber from "../PhoneNumber";
import VerifiedMark from "../VerifiedMark";

const FormItem = Form.Item;
const Option = Select.Option;
const TextArea = Input.TextArea;

type ObjectLiteral<T> = { [x: string]: T; };
type initialValueTypes =
  | string
  | number
  | boolean
  | Moment
  | undefined
  | string[]
  | number[];

interface Props {
  children?: JSX.Element;
  reference: React.RefObject<FormInstance>;
  initialValues?: ObjectLiteral<string>;
  gutter?: [Gutter, Gutter];
  fieldsToHide: string[];
  layout: FormLayout;
  config?: FormProps;
  fields: FormFieldConfig[];
  size?: SizeType;
  style?: React.CSSProperties;
  onFocus?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
}

export function FieldFactory(formFieldConfig: FormFieldConfig): JSX.Element {
  let component: JSX.Element;
  switch (formFieldConfig.inputType) {
    case "checkbox":
      component = (
        <Checkbox
          onChange={formFieldConfig.checkboxChangeHandler}
          checked={formFieldConfig.checkboxCheckedValue}
          style={formFieldConfig.elementStyle}
          disabled={formFieldConfig.disabled}
        >
          {formFieldConfig.value}
        </Checkbox>
      );
      break;
    case "phone":
      component = (
        <PhoneNumber
          style={formFieldConfig.elementStyle}
          disabled={formFieldConfig.disabled as boolean}
          initialValue={formFieldConfig.initialValue as string}
          onChange={formFieldConfig.changeHandler}
        />
      );
      break;
    case "string":
      component = (
        <Input
          disabled={formFieldConfig.disabled}
          style={formFieldConfig.elementStyle}
          placeholder={formFieldConfig.placeholder}
          className={formFieldConfig.className}
          size={formFieldConfig.size}
          onChange={formFieldConfig.onChange}
          defaultValue={formFieldConfig.defaultValue}
        />
      );
      break;
    case "email":
      component = (
        <Input
          disabled={formFieldConfig.disabled}
          style={formFieldConfig.elementStyle}
          placeholder={formFieldConfig.placeholder}
          className={formFieldConfig.className}
        />
      );
      break;
    case "date":
      component = (
        <DateSelector
          {...formFieldConfig.dateOptions}
          disabled={formFieldConfig.disabled}
          initialValue={formFieldConfig.initialValue as string}
          onChange={formFieldConfig.changeHandler}
        />
      );
      break;
    case "defaultDate":
      component = (
        <DatePicker
          className={formFieldConfig.className}
          defaultValue={formFieldConfig.defaultPickerValue}
          format={"YYYY/MM/DD"}
          onChange={formFieldConfig.dateChangeHandler}
          style={formFieldConfig.elementStyle}
          disabled={formFieldConfig.disabled}
          disabledDate={formFieldConfig.disabledDate}
          allowClear
        />
      );
      break;
    case "select":
      component = (
        <Select
          className={formFieldConfig.className}
          disabled={formFieldConfig.disabled}
          showSearch
          value={formFieldConfig.value}
          style={formFieldConfig.elementStyle}
          optionFilterProp="children"
          filterOption={filterMethod}
          placeholder={formFieldConfig.placeholder}
          onChange={formFieldConfig.changeHandler}
          allowClear={formFieldConfig.allowClear}
          onSelect={formFieldConfig.selectHandler}
          defaultValue={formFieldConfig.defaultValue}
        >
          {formFieldConfig.selectOptions &&
            formFieldConfig.selectOptions.map(
              (config: SelectOptionsInterface): React.ReactElement => (
                <Option
                  key={config.id}
                  value={config.value || config.id}
                  title={config.name}
                >
                  {config.isVerified ? (
                    <>
                      <VerifiedMark /> {config.name}
                    </>
                  ) : (
                    config.name
                  )}
                </Option>
              ),
            )}
        </Select>
      );
      break;
    case "multipleSelect":
      component = (
        <Select
          className={formFieldConfig.className}
          disabled={formFieldConfig.disabled}
          showSearch
          mode="multiple"
          style={formFieldConfig.elementStyle}
          optionFilterProp="children"
          filterOption={filterMethod}
          placeholder={formFieldConfig.placeholder}
          onChange={formFieldConfig.changeHandler}
          onSelect={formFieldConfig.selectHandler}
          onFocus={formFieldConfig.onFocus}
        >
          {formFieldConfig.selectOptions &&
            formFieldConfig.selectOptions.map(
              (config: SelectOptionsInterface): React.ReactElement => (
                <Option key={config.id} value={config.id}>
                  {config.name}
                </Option>
              ),
            )}
        </Select>
      );
      break;
    case "optionalSelect":
      component = (
        <Select
          disabled={formFieldConfig.disabled}
          showSearch
          style={formFieldConfig.elementStyle}
          optionFilterProp="children"
          filterOption={filterMethod}
          placeholder={formFieldConfig.placeholder}
          onChange={formFieldConfig.changeHandler}
        >
          <Option key="notProvided" value="Not Provided">
            {notProvidedText}
          </Option>
          {formFieldConfig.selectOptions &&
            formFieldConfig.selectOptions.map(
              (config: SelectOptionsInterface): React.ReactElement => (
                <Option key={config.id} value={config.value || config.id}>
                  {config.name}
                </Option>
              ),
            )}
        </Select>
      );
      break;
    case "tagSelectWithOptGroup":
      if (!formFieldConfig.tagWithOptGroupOptions) {
        message.error("tagWithOptGroupOptions is required");
        return <Fragment />;
      }

      const optGroups = formFieldConfig.tagWithOptGroupOptions;

      component = (
        <Select
          disabled={formFieldConfig.disabled}
          showSearch
          mode="tags"
          style={formFieldConfig.elementStyle}
          optionFilterProp="children"
          filterOption={filterMethod}
          placeholder={formFieldConfig.placeholder}
          onChange={formFieldConfig.changeHandler}
        >
          {Object.keys(optGroups).map((optGroup: string): JSX.Element => {
            const options = optGroups[optGroup];
            return (
              <Select.OptGroup label={optGroup} key={optGroup}>
                {options.map(
                  (config: SelectOptionsInterface): React.ReactElement => (
                    <Option key={config.id} value={config.value || config.id}>
                      {config.name}
                    </Option>
                  ),
                )}
              </Select.OptGroup>
            );
          })}
        </Select>
      );
      break;
    case "radio":
      component = (
        <Radio.Group
          disabled={formFieldConfig.disabled}
          onChange={formFieldConfig.radioChangeHandler}
        >
          <Space direction="horizontal">
            {formFieldConfig.radioOptions &&
              formFieldConfig.radioOptions.map(
                (config: RadioOptionsInterface): React.ReactElement => (
                  <Radio
                    key={config.id}
                    value={config.value}
                    style={formFieldConfig.elementStyle}
                  >
                    {config.name}
                  </Radio>
                ),
              )}
          </Space>
        </Radio.Group>
      );
      break;

    case "button":
      component = (
        <Button
          id={formFieldConfig.id}
          disabled={formFieldConfig.disabled}
          style={formFieldConfig.elementStyle}
          className={formFieldConfig.className}
          onClick={formFieldConfig.handleClick}
        >
          {formFieldConfig.value}
        </Button>
      );
      break;
    case "buttonGroup":
      component = (
        <div
          style={{
            display: "inline-flex",
            gap: "8px",
            ...(formFieldConfig.elementStyle || {}),
          }}
        >
          {formFieldConfig.buttonGroup?.map((btn, idx) => {
            const button = (
              <Button
                type={btn.type}
                danger={btn.danger}
                disabled={btn.disabled}
                loading={btn.loading}
                className={btn.className}
                style={btn.style}
                onClick={btn.onClick}
              >
                {btn.label}
              </Button>
            );

            return btn.tooltip ? (
              <Tooltip
                key={`${formFieldConfig.name}-btn-${idx}`}
                title={btn.tooltip}
              >
                <span style={{ display: "inline-block" }}>{button}</span>
              </Tooltip>
            ) : (
              <React.Fragment key={`${formFieldConfig.name}-btn-${idx}`}>
                {button}
              </React.Fragment>
            );
          })}
        </div>
      );
      break;
    case "number":
      component = (
        <InputNumber
          value={
            typeof formFieldConfig.value === "string"
              ? Number(formFieldConfig.value)
              : (formFieldConfig.value as any)
          }
          placeholder={formFieldConfig.placeholder}
          style={formFieldConfig.elementStyle}
          step={
            formFieldConfig.rule === "integer" && formFieldConfig.step
              ? formFieldConfig.step
              : 0.1
          }
          min={formFieldConfig.min as any}
          max={formFieldConfig.max as any}
          disabled={formFieldConfig.disabled}
          onChange={formFieldConfig.onNumberChange}
        />
      );
      break;
    case "numberArray":
      component = (
        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue, setFieldsValue }) => {
            const values: number[] = getFieldValue(formFieldConfig.name) || [0];

            return (
              <div>
                {values.map((value, index) => (
                  <div
                    key={`amount-${index}`}
                    style={{ display: "flex", gap: 8, marginBottom: 8 }}
                  >
                    <InputNumber
                      placeholder={`Amount ${index + 1}`}
                      style={{ flex: 1, ...formFieldConfig.elementStyle }}
                      min={0}
                      step={0.01}
                      value={value}
                      onChange={(val) => {
                        if (val === null) return;

                        const next = [...values];
                        next[index] = val;

                        setFieldsValue({
                          [formFieldConfig.name]: next,
                        });
                      }}
                      disabled={formFieldConfig.disabled}
                    />

                    {index > 0 && (
                      <Button
                        type="link"
                        danger
                        onClick={() => {
                          const next = values.filter((_, i) => i !== index);
                          setFieldsValue({
                            [formFieldConfig.name]: next,
                          });
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                ))}

                <Button
                  type="dashed"
                  onClick={() =>
                    setFieldsValue({
                      [formFieldConfig.name]: [...values, 0],
                    })
                  }
                  style={{ width: "100%" }}
                  disabled={formFieldConfig.disabled}
                >
                  + Add Amount
                </Button>
              </div>
            );
          }}
        </Form.Item>
      );
      break;
    case "stringArray":
      component = (
        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue, setFieldsValue }) => {
            const values: string[] = getFieldValue(formFieldConfig.name) || [
              "",
            ];

            return (
              <div>
                {values.map((value, index) => (
                  <div
                    key={`${formFieldConfig.name}-${index}`}
                    style={{ display: "flex", gap: 8, marginBottom: 8 }}
                  >
                    <Input
                      placeholder={
                        formFieldConfig.placeholder ||
                        `${formFieldConfig.label} ${index + 1}`
                      }
                      style={{ flex: 1, ...formFieldConfig.elementStyle }}
                      value={value}
                      onChange={(e) => {
                        const next = [...values];
                        next[index] = e.target.value;

                        setFieldsValue({
                          [formFieldConfig.name]: next,
                        });
                      }}
                      disabled={formFieldConfig.disabled}
                    />

                    {index > 0 && (
                      <Button
                        type="link"
                        danger
                        onClick={() => {
                          const next = values.filter((_, i) => i !== index);
                          setFieldsValue({
                            [formFieldConfig.name]: next,
                          });
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                ))}

                <Button
                  type="dashed"
                  onClick={() =>
                    setFieldsValue({
                      [formFieldConfig.name]: [...values, ""],
                    })
                  }
                  style={{ width: "100%" }}
                  disabled={formFieldConfig.disabled}
                >
                  + Add {formFieldConfig.label || "Item"}
                </Button>
              </div>
            );
          }}
        </Form.Item>
      );
      break;
    case "phoneArray":
      component = (
        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue, setFieldsValue }) => {
            const values: string[] = getFieldValue(formFieldConfig.name) || [
              "",
            ];

            return (
              <div>
                {values.map((value, index) => (
                  <div
                    key={`${formFieldConfig.name}-${index}`}
                    style={{ display: "flex", gap: 8, marginBottom: 8 }}
                  >
                    <PhoneNumber
                      style={{ flex: 1, ...formFieldConfig.elementStyle }}
                      initialValue={value}
                      onChange={(phoneValue: string) => {
                        const next = [...values];
                        next[index] = phoneValue;

                        setFieldsValue({
                          [formFieldConfig.name]: next,
                        });
                      }}
                      disabled={formFieldConfig.disabled || false}
                    />

                    {index > 0 && (
                      <Button
                        type="link"
                        danger
                        onClick={() => {
                          const next = values.filter((_, i) => i !== index);
                          setFieldsValue({
                            [formFieldConfig.name]: next,
                          });
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                ))}

                <Button
                  type="dashed"
                  onClick={() =>
                    setFieldsValue({
                      [formFieldConfig.name]: [...values, ""],
                    })
                  }
                  style={{ width: "100%" }}
                  disabled={formFieldConfig.disabled}
                >
                  + Add {formFieldConfig.label || "Phone Number"}
                </Button>
              </div>
            );
          }}
        </Form.Item>
      );
      break;
    case "emailArray":
      component = (
        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue, setFieldsValue }) => {
            const values: string[] = getFieldValue(formFieldConfig.name) || [
              "",
            ];

            return (
              <div>
                {values.map((value, index) => (
                  <div
                    key={`${formFieldConfig.name}-${index}`}
                    style={{ display: "flex", gap: 8, marginBottom: 8 }}
                  >
                    <Input
                      placeholder={
                        formFieldConfig.placeholder || `Email ${index + 1}`
                      }
                      style={{ flex: 1, ...formFieldConfig.elementStyle }}
                      onChange={(e) => {
                        const next = [...values];
                        next[index] = e.target.value;

                        setFieldsValue({
                          [formFieldConfig.name]: next,
                        });
                      }}
                      disabled={formFieldConfig.disabled}
                      value={value}
                    />

                    {index > 0 && (
                      <Button
                        type="link"
                        danger
                        onClick={() => {
                          const next = values.filter((_, i) => i !== index);
                          setFieldsValue({
                            [formFieldConfig.name]: next,
                          });
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                ))}

                <Button
                  type="dashed"
                  onClick={() =>
                    setFieldsValue({
                      [formFieldConfig.name]: [...values, ""],
                    })
                  }
                  style={{ width: "100%" }}
                  disabled={formFieldConfig.disabled}
                >
                  + Add {formFieldConfig.label || "Email"}
                </Button>
              </div>
            );
          }}
        </Form.Item>
      );
      break;
    case "textArea":
      component = (
        <TextArea
          placeholder={formFieldConfig.placeholder}
          autoSize={formFieldConfig.autoSize}
          rows={8}
        />
      );
      break;
    case "fileUpload":
      component = (
        <FileUpload
          {...formFieldConfig.fileUploadProps}
          defaultFileList={formFieldConfig.defaultFileList}
          style={formFieldConfig.style}
          elementStyle={formFieldConfig.elementStyle}
          onChange={formFieldConfig.changeHandler}
          onFileDelete={formFieldConfig.onFileDelete}
        />
      );
      break;
    case "description":
      component = (
        <Descriptions>
          <Descriptions.Item key={formFieldConfig.id}>
            {formFieldConfig.value || formFieldConfig.placeholder}
          </Descriptions.Item>
        </Descriptions>
      );
      break;
    case "alert":
      component = (
        <Alert
          banner
          type={formFieldConfig.alertType || "warning"}
          message={formFieldConfig.value}
          style={formFieldConfig.elementStyle}
        />
      );
      break;
    case "search":
      component = (
        <Select
          className={formFieldConfig.className}
          showSearch
          value={formFieldConfig.value}
          placeholder={formFieldConfig.placeholder}
          disabled={formFieldConfig.disabled}
          notFoundContent={
            formFieldConfig.isSearching ? (
              <Spin size="small" />
            ) : (
              <span>No results found</span>
            )
          }
          filterOption={false}
          onSearch={formFieldConfig.changeHandler}
          onSelect={formFieldConfig.selectHandler}
          onClear={formFieldConfig.clearHandler}
          style={formFieldConfig.elementStyle}
          allowClear={formFieldConfig.allowClear}
          onFocus={formFieldConfig.onFocus}
        >
          {formFieldConfig.selectOptions &&
            formFieldConfig.selectOptions.map(
              (config: SelectOptionsInterface): React.ReactElement => (
                <Option
                  key={`${config.id}-${config.value}`}
                  value={config.value || config.id}
                >
                  {config.name}
                </Option>
              ),
            )}
        </Select>
      );
      break;
    case "multipleSelectSearch":
      component = (
        <Select
          className={formFieldConfig.className}
          disabled={formFieldConfig.disabled}
          showSearch
          mode="multiple"
          style={formFieldConfig.elementStyle}
          optionFilterProp="children"
          filterOption={filterMethod}
          placeholder={formFieldConfig.placeholder}
          onChange={formFieldConfig.changeHandler}
          onSearch={formFieldConfig.searchHandler}
          allowClear={formFieldConfig.allowClear}
          onFocus={formFieldConfig.onFocus}
          notFoundContent={
            formFieldConfig.isSearching ? (
              <Spin size="small" />
            ) : (
              <span>No results found</span>
            )
          }
        >
          {formFieldConfig.selectOptions &&
            formFieldConfig.selectOptions.map(
              (config: SelectOptionsInterface): React.ReactElement => (
                <Option key={config.id} value={config.value || config.id}>
                  {config.name}
                </Option>
              ),
            )}
        </Select>
      );
      break;
    case "switch":
      component = (
        <Switch
          disabled={formFieldConfig.disabled}
          checkedChildren={formFieldConfig.checkedChildren ?? "On"}
          unCheckedChildren={formFieldConfig.unCheckedChildren ?? "Off"}
          onChange={formFieldConfig.switchChangeHandler}
        />
      );
  }

  return component;
}

const NewBaseForm = (props: Props): JSX.Element => {
  const {
    fields,
    fieldsToHide,
    gutter,
    config,
    layout,
    reference,
    size,
    style,
  } = props;

  const initialValues = React.useMemo(() => {
    const values: ObjectLiteral<initialValueTypes> = {};
    fields.forEach((field) => {
      values[field.name] = field.initialValue ?? undefined;
    });
    return values;
  }, [fields]);

  return (
    <React.Fragment>
      <Form
        style={style}
        ref={reference}
        layout={layout}
        {...config}
        size={size}
        initialValues={initialValues}
      >
        {layout === "inline" ? (
          fields.map((field: FormFieldConfig, index): JSX.Element | null => {
            let rules: Rule[] = [];

            if (field.required) {
              rules.push({
                required: true,
                message: field.errorMessage,
                type: field.isArrayField ? "array" : field.rule,
              });
            }

            if (field.pattern) {
              rules.push({
                pattern: field.pattern,
                message: field.patternErrorMessage || "Invalid format",
              });
            }

            if (field.inputType === "emailArray") {
              rules.push({
                validator: (_, value: any[]) => {
                  const emails = (value || []).filter(
                    (v) => v != null && String(v).trim() !== "",
                  );
                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  if (emails.some((v) => !emailRegex.test(v))) {
                    return Promise.reject("Please enter valid email address(es)");
                  }
                  return Promise.resolve();
                },
              });
            }

            if (!fieldsToHide.includes(field.name)) {
              return (
                <Col
                  id={field.name}
                  key={`${field.id || field.name}-${index}`}
                  style={field.style}
                  {...field.colConfig}
                >
                  <FormItem
                    rules={[...rules, ...(field.rules || [])]}
                    label={field.label}
                    style={field.formItemStyle}
                    name={field.name}
                    valuePropName={field.valuePropName}
                    normalize={field.normalize}
                  >
                    {FieldFactory(field)}
                  </FormItem>
                </Col>
              );
            }
            return null;
          })
        ) : (
          <Row gutter={gutter}>
            {fields.map((field: FormFieldConfig, index): JSX.Element | null => {
              let rules: Rule[] = [];

              if (field.required) {
                rules.push({
                  required: true,
                  message: field.errorMessage,
                  type: field.isArrayField ? "array" : field.rule,
                });
              }

              if (field.pattern) {
                rules.push({
                  pattern: field.pattern,
                  message: field.patternErrorMessage || "Invalid format",
                });
              }

              if (field.inputType === "emailArray") {
                rules.push({
                  validator: (_, value: any[]) => {
                    const emails = (value || []).filter(
                      (v) => v != null && String(v).trim() !== "",
                    );
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (emails.some((v) => !emailRegex.test(v))) {
                      return Promise.reject("Please enter valid email address(es)");
                    }
                    return Promise.resolve();
                  },
                });
              }

              if (!fieldsToHide.includes(field.name)) {
                return (
                  <Col
                    id={field.name}
                    key={`${field.name}-${index}`}
                    style={field.style}
                    {...field.colConfig}
                  >
                    <FormItem
                      rules={[...rules, ...(field.rules || [])]}
                      label={field.label}
                      style={field.formItemStyle}
                      name={field.name}
                      valuePropName={field.valuePropName}
                      normalize={field.normalize}
                    >
                      {FieldFactory(field)}
                    </FormItem>
                  </Col>
                );
              }
              return null;
            })}
          </Row>
        )}
        {layout !== "inline" && props.children}
      </Form>
    </React.Fragment>
  );
};

export default NewBaseForm;
